import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  doc,
  setDoc,
  onSnapshot,
  collection,
  addDoc,
  query,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { askGemini } from '../gemini';
import { TARJETA, TOKEN_TARJETA } from '../config';
import PaymentCard from './PaymentCard';
import { Send, Loader2, MessageCircle, Bot, User, ImagePlus, X, Receipt } from 'lucide-react';

const MENSAJE_SIN_RESPUESTA =
  'Por ahora no cuento con la información necesaria para responder eso. Ya avisé al administrador y te responderé por aquí en cuanto la tenga. ¡Gracias por tu paciencia!';

const RESPUESTA_FOTO =
  '¡Recibimos tu foto! 📷 Para enviarte la vista previa de tu retrato ya procesado es necesario realizar primero el pago. Escríbeme “quiero pagar” y te comparto los datos. En cuanto confirmemos el pago, nuestro equipo te contactará por aquí para acordar la imagen.';

const RESPUESTA_COMPROBANTE =
  '¡Gracias! 🧾 Recibimos tu comprobante de pago. Lo verificaremos y nuestro equipo te contactará por este mismo chat para acordar tu retrato.';

// Convierte el texto en nodos de React, transformando:
//  - URLs (http/https/www) en enlaces clicables.
//  - Números de teléfono (8–14 dígitos) en enlaces a WhatsApp (wa.me).
// Evita falsos positivos: los precios con comas (75,000) no califican y los
// números muy largos como la cuenta bancaria (16 dígitos) se dejan como texto.
function renderConLinks(texto) {
  if (!texto) return texto;
  const TOKEN_RE = /(https?:\/\/[^\s]+|www\.[^\s]+)|(\+?\d[\d\s().-]{6,}\d)/g;
  const nodes = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = TOKEN_RE.exec(texto)) !== null) {
    const [full, url, phone] = match;
    const start = match.index;
    if (start > lastIndex) nodes.push(texto.slice(lastIndex, start));

    if (url) {
      // Recortar puntuación final que no es parte de la URL.
      let clean = full;
      let trailing = '';
      const punct = clean.match(/[.,;:!?)\]]+$/);
      if (punct) {
        trailing = punct[0];
        clean = clean.slice(0, -trailing.length);
      }
      const href = clean.startsWith('http') ? clean : `https://${clean}`;
      nodes.push(
        <a
          key={`l${key++}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-blue-300 hover:text-blue-200 break-all"
        >
          {clean}
        </a>
      );
      if (trailing) nodes.push(trailing);
    } else if (phone) {
      const digits = phone.replace(/\D/g, '');
      if (digits.length >= 8 && digits.length <= 14) {
        nodes.push(
          <a
            key={`w${key++}`}
            href={`https://wa.me/${digits}`}
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-emerald-300 hover:text-emerald-200"
          >
            {full.trim()}
          </a>
        );
      } else {
        nodes.push(full);
      }
    }
    lastIndex = start + full.length;
  }

  if (lastIndex < texto.length) nodes.push(texto.slice(lastIndex));
  return nodes;
}

// Extrae las URLs únicas de un texto (normalizadas con https://).
function extraerUrls(texto) {
  if (!texto) return [];
  const RE = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const urls = [];
  let m;
  while ((m = RE.exec(texto)) !== null) {
    const limpio = m[0].replace(/[.,;:!?)\]]+$/, '');
    const href = limpio.startsWith('http') ? limpio : `https://${limpio}`;
    if (!urls.includes(href)) urls.push(href);
  }
  return urls;
}

// Renderiza el texto del asistente. Si contiene el token de la tarjeta (o el
// número escrito directamente), inserta la tarjeta de pago en ese punto.
function renderTexto(texto) {
  if (!texto) return null;
  // Si el modelo escribió el número en vez del token, lo convertimos al token.
  const reNumero = new RegExp(TARJETA.numero.replace(/\s/g, '\\s*'), 'g');
  const normalizado = texto.replace(reNumero, TOKEN_TARJETA);

  if (!normalizado.includes(TOKEN_TARJETA)) {
    return (
      <>
        {renderConLinks(normalizado)}
        {extraerUrls(normalizado).map((u) => (
          <LinkPreview key={u} url={u} />
        ))}
      </>
    );
  }

  const partes = normalizado.split(TOKEN_TARJETA);
  return (
    <>
      {partes.map((p, i) => (
        <React.Fragment key={i}>
          {p && renderConLinks(p)}
          {i < partes.length - 1 && <PaymentCard />}
        </React.Fragment>
      ))}
    </>
  );
}

// Tarjeta de vista previa de un enlace (estilo WhatsApp): imagen + título +
// descripción. Usa microlink.io para obtener los metadatos del sitio (no se
// puede leer el og:image de otro dominio directamente desde el navegador por CORS).
function LinkPreview({ url }) {
  const [data, setData] = useState(null);
  const [fallo, setFallo] = useState(false);

  useEffect(() => {
    let cancelado = false;
    fetch(`https://api.microlink.io/?url=${encodeURIComponent(url)}`)
      .then((r) => r.json())
      .then((j) => {
        if (cancelado) return;
        if (j.status === 'success') setData(j.data);
        else setFallo(true);
      })
      .catch(() => {
        if (!cancelado) setFallo(true);
      });
    return () => {
      cancelado = true;
    };
  }, [url]);

  if (fallo || !data) return null;

  const img = data.image?.url || data.logo?.url;
  let host = '';
  try {
    host = data.publisher || new URL(data.url || url).hostname.replace(/^www\./, '');
  } catch {
    host = data.publisher || '';
  }

  return (
    <a
      href={data.url || url}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-2 block overflow-hidden rounded-xl border border-slate-600/60 bg-slate-900/70 hover:bg-slate-900 transition-colors no-underline"
    >
      {img && (
        <img src={img} alt="" loading="lazy" className="w-full h-36 object-cover" />
      )}
      <div className="p-2.5">
        {data.title && (
          <div className="text-slate-100 text-sm font-semibold leading-snug line-clamp-2">
            {data.title}
          </div>
        )}
        {data.description && (
          <div className="text-slate-400 text-xs mt-1 line-clamp-2">
            {data.description}
          </div>
        )}
        {host && (
          <div className="text-slate-500 text-[11px] mt-1.5 truncate">{host}</div>
        )}
      </div>
    </a>
  );
}

export default function Chat({ phone }) {
  const [mensajes, setMensajes] = useState([]);
  const [contexto, setContexto] = useState('');
  const [texto, setTexto] = useState('');
  const [pensando, setPensando] = useState(false);
  const [error, setError] = useState(null);
  const [imagenPendiente, setImagenPendiente] = useState(null); // File
  const [subiendo, setSubiendo] = useState(false);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  const convRef = doc(db, 'chat_conversations', phone);

  // Asegurar que existe el documento de conversación.
  useEffect(() => {
    setDoc(
      convRef,
      { phone, createdAt: serverTimestamp(), updatedAt: serverTimestamp() },
      { merge: true }
    ).catch((e) => console.error('Error creando conversación:', e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone]);

  // Escuchar mensajes en tiempo real (incluye respuestas que el admin agregue luego).
  useEffect(() => {
    const q = query(
      collection(db, 'chat_conversations', phone, 'messages'),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setMensajes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (e) => console.error('Error escuchando mensajes:', e)
    );
    return () => unsub();
  }, [phone]);

  // Escuchar el contexto configurado por el administrador.
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'chat_config', 'main'), (snap) => {
      setContexto(snap.exists() ? snap.data().context || '' : '');
    });
    return () => unsub();
  }, []);

  // Autoscroll al final.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [mensajes, pensando]);

  const enviar = useCallback(
    async (e) => {
      e?.preventDefault();
      const pregunta = texto.trim();
      if (!pregunta || pensando) return;
      setError(null);
      setTexto('');
      setPensando(true);

      const msgsCol = collection(db, 'chat_conversations', phone, 'messages');

      try {
        // 1. Guardar el mensaje del usuario.
        await addDoc(msgsCol, {
          role: 'user',
          text: pregunta,
          tipo: 'normal',
          createdAt: serverTimestamp(),
        });

        // 2. Construir historial (sin los avisos de "sin respuesta") para Gemini.
        const history = mensajes
          .filter((m) => m.tipo !== 'sin_respuesta' && m.tipo !== 'imagen')
          .slice(-20)
          .map((m) => ({ role: m.role, text: m.text }));

        // 3. Preguntar a Gemini.
        const { answerable, answer } = await askGemini({
          context: contexto,
          history,
          question: pregunta,
        });

        if (answerable && answer) {
          await addDoc(msgsCol, {
            role: 'assistant',
            text: answer,
            tipo: 'normal',
            createdAt: serverTimestamp(),
          });
        } else {
          // 4a. Mensaje de "no tengo la info".
          await addDoc(msgsCol, {
            role: 'assistant',
            text: MENSAJE_SIN_RESPUESTA,
            tipo: 'sin_respuesta',
            createdAt: serverTimestamp(),
          });
          // 4b. Guardar la pregunta para que el administrador la responda.
          await addDoc(collection(db, 'chat_unanswered'), {
            phone,
            question: pregunta,
            status: 'pendiente',
            answer: '',
            createdAt: serverTimestamp(),
            answeredAt: null,
          });
        }

        await setDoc(convRef, { updatedAt: serverTimestamp() }, { merge: true });
      } catch (err) {
        console.error('Error en el chat:', err);
        setError(err.message || 'Ocurrió un error. Intenta de nuevo.');
      } finally {
        setPensando(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [texto, pensando, mensajes, contexto, phone]
  );

  const elegirArchivo = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permitir volver a elegir el mismo archivo
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Solo se pueden enviar imágenes.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('La imagen es muy grande (máximo 10 MB).');
      return;
    }
    setError(null);
    setImagenPendiente(file);
  };

  const enviarImagen = async (esComprobante) => {
    if (!imagenPendiente || subiendo) return;
    setSubiendo(true);
    setError(null);
    const msgsCol = collection(db, 'chat_conversations', phone, 'messages');
    try {
      // 1. Subir la imagen a Storage.
      const ruta = `chat_uploads/${phone}/${Date.now()}_${imagenPendiente.name}`;
      const storageRef = ref(storage, ruta);
      await uploadBytes(storageRef, imagenPendiente);
      const url = await getDownloadURL(storageRef);

      // 2. Guardar el mensaje con la imagen.
      await addDoc(msgsCol, {
        role: 'user',
        tipo: 'imagen',
        text: esComprobante ? 'Comprobante de pago' : 'Foto para mi retrato',
        imageUrl: url,
        esComprobante: !!esComprobante,
        createdAt: serverTimestamp(),
      });

      // 3. Si es comprobante, registrarlo para que el admin lo verifique.
      if (esComprobante) {
        await addDoc(collection(db, 'chat_comprobantes'), {
          phone,
          imageUrl: url,
          status: 'pendiente',
          createdAt: serverTimestamp(),
        });
      }

      // 4. Respuesta automática del asistente.
      await addDoc(msgsCol, {
        role: 'assistant',
        text: esComprobante ? RESPUESTA_COMPROBANTE : RESPUESTA_FOTO,
        tipo: 'normal',
        createdAt: serverTimestamp(),
      });

      await setDoc(convRef, { updatedAt: serverTimestamp() }, { merge: true });
      setImagenPendiente(null);
    } catch (err) {
      console.error('Error subiendo imagen:', err);
      setError('No se pudo enviar la imagen. Intenta de nuevo.');
    } finally {
      setSubiendo(false);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" style={{ height: '100dvh' }}>
      {/* Header */}
      <header className="shrink-0 flex items-center gap-2 px-4 py-3 border-b border-slate-800 bg-slate-900/60 backdrop-blur-sm z-10">
        <div className="w-9 h-9 rounded-xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
          <MessageCircle className="text-blue-400" size={18} />
        </div>
        <div>
          <div className="text-white font-bold text-sm leading-tight">Chat de Rocharamas</div>
          <div className="text-slate-500 text-[11px]">Asistente en línea</div>
        </div>
      </header>

      {/* Mensajes */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
        {mensajes.length === 0 && !pensando && (
          <div className="text-center text-slate-500 text-sm mt-10">
            <Bot size={40} className="mx-auto mb-3 text-slate-700" />
            ¡Hola! Escribe tu pregunta y con gusto te ayudo.
          </div>
        )}

        {mensajes.map((m) => {
          const esUsuario = m.role === 'user';
          const esImagen = m.tipo === 'imagen';
          return (
            <div key={m.id} className={`flex gap-2 ${esUsuario ? 'justify-end' : 'justify-start'}`}>
              {!esUsuario && (
                <div className="w-8 h-8 shrink-0 rounded-full bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
                  <Bot size={16} className="text-blue-400" />
                </div>
              )}
              <div
                className={`max-w-[78%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                  esUsuario
                    ? 'bg-blue-600 text-white rounded-br-md'
                    : m.tipo === 'admin'
                      ? 'bg-emerald-600/20 border border-emerald-500/40 text-emerald-100 rounded-bl-md'
                      : 'bg-slate-800 text-slate-100 border border-slate-700 rounded-bl-md'
                }`}
              >
                {m.tipo === 'admin' && (
                  <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-400 mb-1">
                    Respuesta del equipo
                  </div>
                )}
                {m.enRespuestaA && (
                  <div className="mb-1.5 border-l-2 border-emerald-400/60 bg-black/20 rounded-r-md px-2 py-1">
                    <div className="text-[10px] font-bold text-emerald-300/80 mb-0.5">
                      En respuesta a
                    </div>
                    <div className="text-xs text-slate-300/90 line-clamp-2 italic">
                      {m.enRespuestaA}
                    </div>
                  </div>
                )}

                {esImagen ? (
                  <div>
                    {m.esComprobante && (
                      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-amber-200/90 mb-1">
                        <Receipt size={12} /> Comprobante de pago
                      </div>
                    )}
                    <a href={m.imageUrl} target="_blank" rel="noopener noreferrer">
                      <img
                        src={m.imageUrl}
                        alt={m.text || 'imagen'}
                        loading="lazy"
                        className="rounded-xl max-h-64 w-auto object-cover border border-white/10"
                      />
                    </a>
                  </div>
                ) : (
                  renderTexto(m.text)
                )}
              </div>
              {esUsuario && (
                <div className="w-8 h-8 shrink-0 rounded-full bg-slate-700 flex items-center justify-center">
                  <User size={16} className="text-slate-300" />
                </div>
              )}
            </div>
          );
        })}

        {pensando && (
          <div className="flex gap-2 justify-start">
            <div className="w-8 h-8 shrink-0 rounded-full bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
              <Bot size={16} className="text-blue-400" />
            </div>
            <div className="px-4 py-3 rounded-2xl bg-slate-800 border border-slate-700 rounded-bl-md">
              <Loader2 size={16} className="animate-spin text-slate-400" />
            </div>
          </div>
        )}
      </div>

      {/* Panel de imagen pendiente: elegir si es foto o comprobante */}
      {imagenPendiente && (
        <div className="shrink-0 border-t border-slate-800 bg-slate-900/80 backdrop-blur-sm p-3">
          <div className="flex items-center gap-3">
            <img
              src={URL.createObjectURL(imagenPendiente)}
              alt="vista previa"
              className="w-14 h-14 rounded-lg object-cover border border-slate-700"
            />
            <div className="flex-1">
              <div className="text-slate-200 text-xs font-semibold mb-2">
                ¿Qué estás enviando?
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => enviarImagen(false)}
                  disabled={subiendo}
                  className="bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white text-xs font-bold py-1.5 px-3 rounded-lg flex items-center gap-1.5"
                >
                  {subiendo ? <Loader2 className="animate-spin" size={13} /> : <ImagePlus size={13} />}
                  Foto para mi retrato
                </button>
                <button
                  onClick={() => enviarImagen(true)}
                  disabled={subiendo}
                  className="bg-amber-600 hover:bg-amber-500 disabled:opacity-60 text-white text-xs font-bold py-1.5 px-3 rounded-lg flex items-center gap-1.5"
                >
                  {subiendo ? <Loader2 className="animate-spin" size={13} /> : <Receipt size={13} />}
                  Comprobante de pago
                </button>
              </div>
            </div>
            <button
              onClick={() => setImagenPendiente(null)}
              disabled={subiendo}
              className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 disabled:opacity-60"
              title="Cancelar"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      <form onSubmit={enviar} className="shrink-0 border-t border-slate-800 bg-slate-900/60 backdrop-blur-sm p-3">
        {error && (
          <div className="mb-2 p-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs text-center">
            {error}
          </div>
        )}
        <div className="flex items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={elegirArchivo}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            title="Enviar imagen"
            className="w-12 h-12 shrink-0 rounded-full bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 flex items-center justify-center transition-colors"
          >
            <ImagePlus size={18} />
          </button>
          <textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                enviar();
              }
            }}
            rows={1}
            placeholder="Escribe tu mensaje…"
            className="flex-1 resize-none bg-slate-800 border border-slate-700 rounded-2xl text-white px-4 py-3 text-sm focus:outline-none focus:border-blue-500 max-h-32"
          />
          <button
            type="submit"
            disabled={pensando || !texto.trim()}
            className="w-12 h-12 shrink-0 rounded-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white flex items-center justify-center transition-colors"
          >
            {pensando ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
          </button>
        </div>
      </form>
    </div>
  );
}
